import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import ExtensionIcon from "@mui/icons-material/Extension";
import IosShareIcon from "@mui/icons-material/IosShare";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PauseIcon from "@mui/icons-material/Pause";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FaShoppingBasket } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { SURFACE } from "../../../dark-theme/palette";
import { trackEvent } from "../../../services/GoogleAnalytics";
import { decryptText } from "../../../services/CryptoUtil";
import { PUZZLE_GRID_COLORS } from "../../../services/ColorService";
import Logger from "../../../services/Logger";
import AdminColorBasketMiniMap, {
    colorBasketNumberBadgeSx,
} from "../../../admin/jigsaw/play/AdminColorBasketMiniMap";
import { buildScatterPositions } from "../../puzzles/jigsaw/CanvasScattered";
import CanvasBoard from "../../puzzles/jigsaw/CanvasBoard";
import { BOARD_SIZE, PieceVisual, pieceVisualRenderSizePx } from "../../puzzles/jigsaw/PieceVisual";
import { usePagesTranslation } from "../../locales";
import { getLocalizedHomeBrowseStoryTeaser } from "../../browse-puzzles/homePuzzleRoUtils";
import encryptedSampleEn from "./examples/sample_en_jigsaw_encrypted.json";

const TOOLBAR_ICON_SX = { color: "#3BA666", fontSize: "1.35rem", mt: 0.25, flexShrink: 0 };

/** Illustrative baskets for the 6×6 howto sample (live play uses 9×9+). */
const HOWTO_MOCK_BASKETS = [
    { index: 0, dominantColorHex: "#5B9BD5", pieceIds: [0, 1, 2, 3, 4, 5] },
    { index: 1, dominantColorHex: "#E8A45A", pieceIds: [6, 7, 8, 9, 10, 11] },
    { index: 2, dominantColorHex: "#3BA666", pieceIds: [12, 13, 14, 15, 16, 17] },
    { index: 3, dominantColorHex: "#2E7D57", pieceIds: [18, 19, 20, 21, 22, 23] },
    { index: 4, dominantColorHex: "#8B5A2B", pieceIds: [24, 25, 26, 27, 28, 29] },
    { index: 5, dominantColorHex: "#C98400", pieceIds: [30, 31, 32, 33, 34, 35] },
];

function ToolbarItem({ icon, label, body }) {
    return (
        <Box
            component="li"
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                mb: 1.75,
            }}
        >
            {icon}
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1.05rem", lineHeight: 1.7, mt: 0.25 }}>
                    {body}
                </Typography>
            </Box>
        </Box>
    );
}

function newBoardPieceId() {
    return `bp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * How-to for jigsaw puzzles. Routed from `/howto/puzzle/jigsaw`.
 */
export default function HowToJigsaw() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [sampleData, setSampleData] = useState(null);
    const [boardPieces, setBoardPieces] = useState([]);
    const [scatterPositions, setScatterPositions] = useState({});
    const [dragging, setDragging] = useState(null);
    const [boardScale, setBoardScale] = useState(1);
    /** Selected mock basket for the howto basket-play preview (right column). */
    const [demoBasketIndex, setDemoBasketIndex] = useState(0);
    const [demoBasketBoardPieces, setDemoBasketBoardPieces] = useState([]);

    const boardRef = useRef(null);
    const scatteredRef = useRef(null);
    const demoBasketTrayRef = useRef(null);

    useEffect(() => {
        trackEvent("entered", { name: "HowToJigsaw" });
    }, []);

    useEffect(() => {
        try {
            const encryptedText =
                typeof encryptedSampleEn === "string"
                    ? encryptedSampleEn
                    : encryptedSampleEn?.data || encryptedSampleEn?.encrypted || Object.values(encryptedSampleEn)[0];
            if (!encryptedText || typeof encryptedText !== "string") return;
            const decrypted = decryptText(encryptedText.trim());
            if (!decrypted) return;
            const parsed = JSON.parse(decrypted);
            setSampleData(parsed);
            const jigsaw = parsed?.jigsaw;
            if (jigsaw?.tileSize) {
                const grid = jigsaw.tileSize;
                const total = grid * grid;
                const key = jigsaw.id || String(grid);
                setScatterPositions(buildScatterPositions(total, grid, key));
            }
        } catch (e) {
            Logger.error("HowToJigsaw: failed to decrypt sample", e);
        }
    }, []);

    const jigsaw = sampleData?.jigsaw ?? null;
    const story = sampleData?.story ?? null;
    const imageUrl = story?.imageUrl ?? story?.imagePreviewUrl ?? story?.imageIcon400Url ?? null;
    const exampleTeaser = getLocalizedHomeBrowseStoryTeaser({ story }, "english");
    const paths = jigsaw?.piecePaths?.pieces ?? null;
    const grid = jigsaw?.tileSize ?? 0;
    const total = grid * grid;

    const piecesNotOnBoard = Array.from({ length: total }, (_, i) => i).filter(
        (p) => !boardPieces.some((b) => b.pieceIndex === p),
    );

    const shuffledPieces = useMemo(() => {
        const arr = [...piecesNotOnBoard];
        const seed = grid * 31 + arr.length * 97;
        for (let i = arr.length - 1; i > 0; i--) {
            const x = Math.sin((seed + i * 7919 + arr[i] * 104729) * 12.9898 + 78.233) * 43758.5453;
            const j = Math.floor((x - Math.floor(x)) * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }, [piecesNotOnBoard, grid]);

    const demoBasket = HOWTO_MOCK_BASKETS[demoBasketIndex] || HOWTO_MOCK_BASKETS[0];
    const demoBasketPieceIds = demoBasket?.pieceIds ?? [];
    const demoBasketHex = demoBasket?.dominantColorHex || "#3BA666";

    // Seed basket-play preview: first half on the board at home, rest in the tray.
    useEffect(() => {
        const basket = HOWTO_MOCK_BASKETS[demoBasketIndex] || HOWTO_MOCK_BASKETS[0];
        const ids = basket?.pieceIds ?? [];
        if (!grid || ids.length === 0) {
            setDemoBasketBoardPieces([]);
            return;
        }
        const cellPx = BOARD_SIZE / grid;
        const onBoardCount = Math.max(1, Math.floor(ids.length / 2));
        const placed = ids.slice(0, onBoardCount).map((pieceIndex) => ({
            id: `howto-basket-${demoBasketIndex}-${pieceIndex}`,
            pieceIndex,
            x: (pieceIndex % grid) * cellPx,
            y: Math.floor(pieceIndex / grid) * cellPx,
        }));
        setDemoBasketBoardPieces(placed);
    }, [grid, demoBasketIndex]);

    const demoBasketTrayIds = useMemo(() => {
        const onBoard = new Set(demoBasketBoardPieces.map((b) => b.pieceIndex));
        return demoBasketPieceIds.filter((id) => !onBoard.has(id));
    }, [demoBasketBoardPieces, demoBasketPieceIds]);

    const demoRegionGuide = useMemo(
        () => ({
            pieceIndexes: demoBasketPieceIds,
            colorHex: demoBasketHex,
        }),
        [demoBasketPieceIds, demoBasketHex],
    );

    const handleMouseDown = useCallback((e, pieceIndex, opts) => {
        const { fromScattered, boardLane, boardId, boardX, boardY, scatterX, scatterY } = opts;
        e.preventDefault();
        if (!grid) return;
        const cp = BOARD_SIZE / grid;
        let grabDx = cp / 2;
        let grabDy = cp / 2;
        const elRect = e.currentTarget.getBoundingClientRect();
        if (fromScattered) {
            grabDx = cp / 2;
            grabDy = cp / 2;
        } else if (boardLane === "scatter" && boardRef.current && boardX != null && boardY != null) {
            const rect = boardRef.current.getBoundingClientRect();
            const s = rect.width / BOARD_SIZE || 1;
            grabDx = (e.clientX - rect.left) / s - boardX;
            grabDy = (e.clientY - rect.top) / s - boardY;
        }
        setDragging({
            pieceIndex,
            x: e.clientX,
            y: e.clientY,
            startX: e.clientX,
            startY: e.clientY,
            elLeft: elRect.left,
            elTop: elRect.top,
            fromScattered: !!fromScattered,
            boardLane: boardLane ?? null,
            boardId: boardId ?? null,
            grabDx,
            grabDy,
        });
    }, [grid, boardScale]);

    useEffect(() => {
        if (!dragging || !grid) return;
        const cellPx = BOARD_SIZE / grid;
        const tabMargin = cellPx * 0.35;

        const inBoard = (cx, cy, ref) => {
            if (!ref?.current) return false;
            const r = ref.current.getBoundingClientRect();
            return cx >= r.left && cx < r.right && cy >= r.top && cy < r.bottom;
        };

        const inRect = (cx, cy, ref) => {
            if (!ref?.current) return false;
            const r = ref.current.getBoundingClientRect();
            return cx >= r.left && cx < r.right && cy >= r.top && cy < r.bottom;
        };

        const applySnap = (nx, ny, pIndex) => {
            const homeX = (pIndex % grid) * cellPx;
            const homeY = Math.floor(pIndex / grid) * cellPx;
            const snapTol = cellPx * 0.38;
            let x = nx, y = ny;
            if (Math.abs(x - homeX) <= snapTol && Math.abs(y - homeY) <= snapTol) { x = homeX; y = homeY; }
            return { x, y };
        };

        const onMove = (e) => setDragging((p) => (p ? { ...p, x: e.clientX, y: e.clientY } : null));
        const onUp = (e) => {
            const { pieceIndex, fromScattered, boardLane, boardId, grabDx, grabDy } = dragging;
            const cx = e.clientX, cy = e.clientY;

            if (inBoard(cx, cy, boardRef)) {
                const rect = boardRef.current.getBoundingClientRect();
                const s = rect.width / BOARD_SIZE || 1;
                let nx = (cx - rect.left) / s - grabDx;
                let ny = (cy - rect.top) / s - grabDy;
                nx = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPx + tabMargin, nx));
                ny = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPx + tabMargin, ny));
                const snapped = applySnap(nx, ny, pieceIndex);
                if (fromScattered) {
                    setBoardPieces((prev) => [...prev, { id: newBoardPieceId(), pieceIndex, x: snapped.x, y: snapped.y }]);
                } else if (boardLane === "scatter" && boardId) {
                    setBoardPieces((prev) => prev.map((b) => (b.id === boardId ? { ...b, x: snapped.x, y: snapped.y } : b)));
                }
                setDragging(null);
                return;
            }

            // Drop back to scattered
            if (fromScattered && inRect(cx, cy, scatteredRef)) {
                const rect = scatteredRef.current.getBoundingClientRect();
                let nx = cx - rect.left - grabDx;
                let ny = cy - rect.top - grabDy;
                nx = Math.max(0, Math.min(BOARD_SIZE - cellPx, nx));
                ny = Math.max(0, Math.min(BOARD_SIZE - cellPx, ny));
                setScatterPositions((prev) => ({ ...prev, [pieceIndex]: { x: nx, y: ny } }));
                setDragging(null);
                return;
            }

            // Remove from board back to scattered
            if (!fromScattered && boardId && inRect(cx, cy, scatteredRef)) {
                setBoardPieces((prev) => prev.filter((b) => b.id !== boardId));
            }

            setDragging(null);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [dragging, grid]);

    const trayCellPx = grid > 0 ? BOARD_SIZE / grid : 0;
    const trayNativeRenderPx =
        trayCellPx > 0 ? pieceVisualRenderSizePx(trayCellPx) : 48;
    const trayScale = boardScale > 0 ? boardScale : 1;
    const trayScaledPx = Math.max(36, Math.round(trayNativeRenderPx * trayScale));
    const trayLayoutCollapse = (trayScaledPx - trayNativeRenderPx) / 2;

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                {t("howto.jigsaw.title")}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 3 }}>
                {t("howto.jigsaw.intro")}
            </Typography>

            {/* Interactive example */}
            {paths && imageUrl && grid > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                        {t("howto.jigsaw.part-example-title")}
                    </Typography>
                    {exampleTeaser.storyTitle ? (
                        <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                            {exampleTeaser.storyTitle}
                        </Typography>
                    ) : null}
                    <Grid container spacing={2} sx={{ width: "100%", alignItems: "flex-start" }}>
                        {/* Original image */}
                        <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="subtitle2">{t("howto.jigsaw.original-image")}</Typography>
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    aspectRatio: "1 / 1",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    pointerEvents: "none",
                                    userSelect: "none",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                />
                                <Box
                                    component="svg"
                                    sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
                                    viewBox="0 0 1536 1536"
                                    preserveAspectRatio="none"
                                >
                                    {paths.map((piece, index) => (
                                        <path
                                            key={index}
                                            d={piece.path}
                                            fill="none"
                                            stroke="rgba(255, 255, 255, 0.75)"
                                            strokeWidth="3"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Grid>

                        {/* Board */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <CanvasBoard
                                boardRef={boardRef}
                                boardPieces={boardPieces}
                                grid={grid}
                                imageUrl={imageUrl}
                                paths={paths}
                                dragging={dragging}
                                showDragOutline={Boolean(dragging && (dragging.fromScattered || dragging.boardLane === "scatter"))}
                                headerTitle={t("howto.jigsaw.board")}
                                placedCount={boardPieces.length}
                                total={total}
                                isSolved={false}
                                showPieceGridCoords={false}
                                onScaleChange={setBoardScale}
                                onBoardPieceMouseDown={(e, pieceIndex, meta) =>
                                    handleMouseDown(e, pieceIndex, { boardLane: "scatter", ...meta })
                                }
                            />
                        </Grid>

                        {/* Horizontal piece list — aligned under the board */}
                        <Grid size={{ xs: 0, md: 6 }} />
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                ref={scatteredRef}
                                sx={{
                                    width: "100%",
                                    overflowX: "auto",
                                    overflowY: "hidden",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    mt: "10px",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1,
                                    py: 1.25,
                                    boxSizing: "border-box",
                                    minHeight: trayScaledPx + 28,
                                }}
                            >
                                {shuffledPieces.map((pieceIndex) => {
                                    const isDraggingThis =
                                        dragging?.pieceIndex === pieceIndex && dragging?.fromScattered;
                                    return (
                                        <Box
                                            key={pieceIndex}
                                            sx={{
                                                cursor: "grab",
                                                opacity: isDraggingThis ? 0.3 : 1,
                                                flexShrink: 0,
                                                width: trayNativeRenderPx,
                                                height: trayNativeRenderPx,
                                                // Collapse layout to the scaled visual size so the tray
                                                // height matches what you see (tabs stay fully visible).
                                                transform: `scale(${trayScale})`,
                                                transformOrigin: "center center",
                                                m: `${trayLayoutCollapse}px`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                overflow: "visible",
                                                boxSizing: "border-box",
                                            }}
                                            onMouseDown={(e) => {
                                                const elRect = e.currentTarget.getBoundingClientRect();
                                                const parentRect = scatteredRef.current?.getBoundingClientRect();
                                                const scatterX = parentRect
                                                    ? elRect.left - parentRect.left
                                                    : 0;
                                                const scatterY = parentRect
                                                    ? elRect.top - parentRect.top
                                                    : 0;
                                                handleMouseDown(e, pieceIndex, {
                                                    fromScattered: true,
                                                    scatterX,
                                                    scatterY,
                                                });
                                            }}
                                        >
                                            <PieceVisual
                                                pieceIndex={pieceIndex}
                                                imageUrl={imageUrl}
                                                grid={grid}
                                                paths={paths}
                                                showGridCoords={false}
                                                anchorToCell={false}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => navigate("/puzzles/jigsaw")}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                backgroundColor: PUZZLE_GRID_COLORS.BUTTON_PRIMARY_GREEN,
                                "&:hover": { backgroundColor: PUZZLE_GRID_COLORS.BUTTON_PRIMARY_GREEN_HOVER },
                            }}
                        >
                            {t("howto.jigsaw.browse-link")}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Dragging ghost */}
            {dragging && paths && (() => {
                const deltaX = dragging.x - dragging.startX;
                const deltaY = dragging.y - dragging.startY;
                const cp = BOARD_SIZE / grid;
                const circleDiameter = cp * 1.7 * boardScale;
                const ghostLeft = dragging.elLeft + circleDiameter / 2 - (cp / 2) * boardScale + deltaX;
                const ghostTop = dragging.elTop + circleDiameter / 2 - (cp / 2) * boardScale + deltaY;
                return (
                    <div
                        style={{
                            position: "fixed",
                            left: ghostLeft,
                            top: ghostTop,
                            pointerEvents: "none",
                            zIndex: 9999,
                            transform: `scale(${boardScale})`,
                            transformOrigin: "top left",
                        }}
                    >
                        <PieceVisual
                            pieceIndex={dragging.pieceIndex}
                            imageUrl={imageUrl}
                            grid={grid}
                            paths={paths}
                            showGridCoords={false}
                        />
                    </div>
                );
            })()}

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                    {t("howto.jigsaw.part-howto-title")}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                    {t("howto.jigsaw.part-howto-body")}
                </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                    {t("howto.jigsaw.part-levels-title")}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                    {t("howto.jigsaw.part-levels-body")}
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.level-easy")}</Typography></li>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.level-medium")}</Typography></li>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.level-hard")}</Typography></li>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.level-pro")}</Typography></li>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.level-elite")}</Typography></li>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.level-insane")}</Typography></li>
                </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                    {t("howto.jigsaw.part-controls-title")}
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.control-drag")}</Typography></li>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.control-snap")}</Typography></li>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.control-toggle")}</Typography></li>
                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.jigsaw.control-reference")}</Typography></li>
                </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                    {t("howto.jigsaw.part-toolbar-title")}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 2 }}>
                    {t("howto.jigsaw.part-toolbar-intro")}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                    <ToolbarItem
                        icon={<ArrowBackIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.toolbar-back-label")}
                        body={t("howto.jigsaw.toolbar-back-body")}
                    />
                    <ToolbarItem
                        icon={<IosShareIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.toolbar-share-label")}
                        body={t("howto.jigsaw.toolbar-share-body")}
                    />
                    <ToolbarItem
                        icon={<PauseIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.toolbar-timer-label")}
                        body={t("howto.jigsaw.toolbar-timer-body")}
                    />
                    <ToolbarItem
                        icon={<CleaningServicesIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.toolbar-sweep-label")}
                        body={t("howto.jigsaw.toolbar-sweep-body")}
                    />
                    <ToolbarItem
                        icon={<LightbulbOutlinedIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.toolbar-hint-label")}
                        body={t("howto.jigsaw.toolbar-hint-body")}
                    />
                    <ToolbarItem
                        icon={<FaShoppingBasket size={22} color="#3BA666" style={{ marginTop: 4, flexShrink: 0 }} />}
                        label={t("howto.jigsaw.toolbar-baskets-label", "Color baskets")}
                        body={t(
                            "howto.jigsaw.toolbar-baskets-body",
                            "Opens the color map and basket list (Medium 9×9 and larger). Assemble one color, then deploy it to the main board.",
                        )}
                    />
                    <ToolbarItem
                        icon={<SettingsIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.toolbar-settings-label")}
                        body={t("howto.jigsaw.toolbar-settings-body")}
                    />
                </Box>
                <Box component="ul" sx={{ listStyle: "none", m: 0, pl: { xs: 0, sm: 5 }, mt: 0.5 }}>
                    <ToolbarItem
                        icon={<AccountBalanceWalletOutlinedIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.settings-balance-label")}
                        body={t("howto.jigsaw.settings-balance-body")}
                    />
                    <ToolbarItem
                        icon={<ImageOutlinedIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.settings-reference-label")}
                        body={t("howto.jigsaw.settings-reference-body")}
                    />
                    <ToolbarItem
                        icon={<PaletteOutlinedIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.settings-mat-label")}
                        body={t("howto.jigsaw.settings-mat-body")}
                    />
                    <ToolbarItem
                        icon={<ExtensionIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.settings-die-lines-label")}
                        body={t("howto.jigsaw.settings-die-lines-body")}
                    />
                    <ToolbarItem
                        icon={<MusicNoteIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.jigsaw.settings-audio-label")}
                        body={t("howto.jigsaw.settings-audio-body")}
                    />
                </Box>
            </Box>

            {/* Color baskets */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                    {t("howto.jigsaw.part-baskets-title", "Color baskets")}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 1.5 }}>
                    {t(
                        "howto.jigsaw.part-baskets-intro",
                        "On Medium and larger boards (9×9 and up), pieces can be grouped by similar colors into baskets. Assemble one color at a time, then deploy the finished group onto the main board.",
                    )}
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                    <li>
                        <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t(
                                "howto.jigsaw.baskets-step-open",
                                "Open color baskets from the play board to see the color map and basket list.",
                            )}
                        </Typography>
                    </li>
                    <li>
                        <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t(
                                "howto.jigsaw.baskets-step-pick",
                                "Pick a color basket — only that basket’s pieces appear in the tray.",
                            )}
                        </Typography>
                    </li>
                    <li>
                        <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t(
                                "howto.jigsaw.baskets-step-assemble",
                                "Assemble every piece for that color on the basket board.",
                            )}
                        </Typography>
                    </li>
                    <li>
                        <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t(
                                "howto.jigsaw.baskets-step-deploy",
                                "Tap Deploy to send those pieces onto the main puzzle, then choose the next color.",
                            )}
                        </Typography>
                    </li>
                </Box>

                {imageUrl && grid > 0 && paths ? (
                    <Grid container spacing={2} sx={{ width: "100%", alignItems: "stretch" }}>
                        {/* Left: color basket select */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                sx={{
                                    p: 2,
                                    height: "100%",
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor: "divider",
                                    bgcolor: SURFACE.sunken,
                                    boxSizing: "border-box",
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700, mb: 1.5, textAlign: "center" }}
                                >
                                    {t("howto.jigsaw.baskets-demo-title", "Color Baskets")}
                                </Typography>
                                <Stack spacing={2} alignItems="center">
                                    <Box
                                        sx={{
                                            width: "100%",
                                            maxWidth: 280,
                                            mx: "auto",
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <AdminColorBasketMiniMap
                                            baskets={HOWTO_MOCK_BASKETS}
                                            imageUrl={imageUrl}
                                            grid={grid}
                                            size={null}
                                            compact
                                            oneBasedLabels
                                            highlightBasketIndex={demoBasketIndex}
                                            emphasizeOnly
                                            onBasketClick={(index) => setDemoBasketIndex(Number(index))}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexWrap: "wrap",
                                            justifyContent: "center",
                                            gap: 1,
                                        }}
                                    >
                                        {HOWTO_MOCK_BASKETS.map((basket) => {
                                            const hex = basket.dominantColorHex;
                                            const pieceCount = basket.pieceIds.length;
                                            const displayN = basket.index + 1;
                                            const selected = basket.index === demoBasketIndex;
                                            return (
                                                <Box
                                                    key={basket.index}
                                                    component="button"
                                                    type="button"
                                                    onClick={() => setDemoBasketIndex(basket.index)}
                                                    aria-pressed={selected}
                                                    aria-label={t("howto.jigsaw.baskets-select-aria", {
                                                        n: displayN,
                                                        defaultValue: "Select basket {{n}}",
                                                    })}
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        gap: 0.35,
                                                        width: 56,
                                                        p: 0,
                                                        m: 0,
                                                        border: "none",
                                                        background: "none",
                                                        cursor: "pointer",
                                                        WebkitTapHighlightColor: "transparent",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: "relative",
                                                            width: 48,
                                                            height: 48,
                                                            borderRadius: 1.25,
                                                            bgcolor: hex,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            boxShadow: selected
                                                                ? "0 0 0 2px #1976d2, 0 1px 2px rgba(0,0,0,0.18)"
                                                                : "0 1px 2px rgba(0,0,0,0.18)",
                                                            outline: selected
                                                                ? "2px solid rgba(25, 118, 210, 0.35)"
                                                                : "none",
                                                            outlineOffset: 2,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                top: 2,
                                                                left: 2,
                                                                ...colorBasketNumberBadgeSx({
                                                                    size: 16,
                                                                    highlighted: selected,
                                                                }),
                                                            }}
                                                        >
                                                            {displayN}
                                                        </Box>
                                                        <FaShoppingBasket
                                                            size={18}
                                                            color="#fff"
                                                            aria-hidden
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: "text.secondary",
                                                            fontSize: 10,
                                                            textAlign: "center",
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {t("howto.jigsaw.baskets-piece-count", {
                                                            count: pieceCount,
                                                            defaultValue: "{{count}} pieces",
                                                        })}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Stack>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: "block",
                                        mt: 1.5,
                                        textAlign: "center",
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {t(
                                        "howto.jigsaw.baskets-demo-caption",
                                        "Example color map (illustrative). Live color baskets start on Medium 9×9 and larger boards.",
                                    )}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.75,
                                        mt: 1,
                                        color: "text.secondary",
                                    }}
                                >
                                    <FaShoppingBasket size={16} color="#3BA666" aria-hidden />
                                    <Typography variant="caption" sx={{ lineHeight: 1.35 }}>
                                        {t(
                                            "howto.jigsaw.baskets-toolbar-note",
                                            "Find the basket icon on the play toolbar to open color baskets.",
                                        )}
                                    </Typography>
                                    <RocketLaunchIcon
                                        sx={{ fontSize: 16, color: "primary.main" }}
                                        aria-hidden
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        {/* Right: basket play page preview */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                sx={{
                                    p: 2,
                                    height: "100%",
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor: "divider",
                                    bgcolor: SURFACE.sunken,
                                    boxSizing: "border-box",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.25,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700, textAlign: "center" }}
                                >
                                    {t("howto.jigsaw.baskets-play-title", "Basket play")}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        alignSelf: "center",
                                        gap: 0.75,
                                        px: 1,
                                        py: 0.4,
                                        borderRadius: 1.5,
                                        bgcolor: SURFACE.card,
                                        border: "1px solid",
                                        borderColor: SURFACE.border,
                                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            color: "text.primary",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {t("howto.jigsaw.baskets-play-chip-label", "Basket")}
                                    </Typography>
                                    <Box
                                        aria-hidden
                                        sx={{
                                            ...colorBasketNumberBadgeSx({ size: 22 }),
                                            flexShrink: 0,
                                        }}
                                    >
                                        {demoBasketIndex + 1}
                                    </Box>
                                    <Box
                                        aria-hidden
                                        title={demoBasketHex}
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            minWidth: 22,
                                            borderRadius: 0.75,
                                            bgcolor: demoBasketHex,
                                            border: "1.5px solid rgba(255,255,255,0.85)",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.28)",
                                            flexShrink: 0,
                                            boxSizing: "border-box",
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            color: "text.primary",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {t("howto.jigsaw.baskets-piece-count", {
                                            count: demoBasketPieceIds.length,
                                            defaultValue: "{{count}} pieces",
                                        })}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: 320,
                                        mx: "auto",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        border: 1,
                                        borderColor: "divider",
                                        bgcolor: "background.paper",
                                    }}
                                >
                                    <CanvasBoard
                                        boardPieces={demoBasketBoardPieces}
                                        grid={grid}
                                        imageUrl={imageUrl}
                                        paths={paths}
                                        placedCount={demoBasketBoardPieces.length}
                                        total={demoBasketPieceIds.length}
                                        isSolved={false}
                                        showHeader={false}
                                        readOnly
                                        showDieLines={false}
                                        regionGuide={demoRegionGuide}
                                        cropToRegionGuide
                                        cropMaxHeight={220}
                                    />
                                </Box>
                                <Box
                                    ref={demoBasketTrayRef}
                                    sx={{
                                        width: "100%",
                                        maxWidth: 320,
                                        mx: "auto",
                                        minHeight: 56,
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: "divider",
                                        bgcolor: "background.paper",
                                        p: 0.75,
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    {demoBasketTrayIds.map((pieceIndex) => {
                                        const cellPx = BOARD_SIZE / grid;
                                        const nativePx = pieceVisualRenderSizePx(cellPx);
                                        const scale = 0.42;
                                        const layoutCollapse = (nativePx * scale - nativePx) / 2;
                                        return (
                                            <Box
                                                key={`howto-basket-tray-${pieceIndex}`}
                                                sx={{
                                                    flexShrink: 0,
                                                    width: nativePx,
                                                    height: nativePx,
                                                    transform: `scale(${scale})`,
                                                    transformOrigin: "center center",
                                                    m: `${layoutCollapse}px`,
                                                    pointerEvents: "none",
                                                    userSelect: "none",
                                                }}
                                            >
                                                <PieceVisual
                                                    pieceIndex={pieceIndex}
                                                    imageUrl={imageUrl}
                                                    grid={grid}
                                                    paths={paths}
                                                    showGridCoords={false}
                                                    anchorToCell={false}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", textAlign: "center", lineHeight: 1.4 }}
                                >
                                    {t(
                                        "howto.jigsaw.baskets-play-caption",
                                        "Basket play shows only that color’s region. Place every piece, then Deploy to the main board.",
                                    )}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                ) : null}
            </Box>
        </Container>
    );
}
